import React, { useState, useEffect } from 'react';
import { Target, AlertTriangle, AlertCircle, Info, ChevronLeft } from 'lucide-react';
import { cn } from '@/lib/utils';

interface PilotagePanelCollapsedProps {
  isVisible: boolean;
  onExpand: () => void;
  compteurs: {
    critique: number;
    attention: number;
    info: number;
    total: number;
  };
}

/**
 * Onglet plié du Centre de Pilotage (90px × auto)
 * Positionné au-dessus du panneau Planning
 */
const PilotagePanelCollapsed = ({
  isVisible,
  onExpand,
  compteurs,
}: PilotagePanelCollapsedProps) => {
  const [shouldBounce, setShouldBounce] = useState(false);

  // Animation bounce à l'arrivée sur le dashboard
  useEffect(() => {
    if (isVisible) {
      // Petit délai avant de lancer l'animation
      const startTimer = setTimeout(() => setShouldBounce(true), 300);
      // Stopper l'animation après 1.5s
      const stopTimer = setTimeout(() => setShouldBounce(false), 1800);
      return () => {
        clearTimeout(startTimer);
        clearTimeout(stopTimer);
      };
    }
  }, []);

  if (!isVisible) return null;

  // Couleur du badge selon la priorité
  const badgeColor =
    compteurs.critique > 0
      ? 'bg-red-500'
      : compteurs.attention > 0
      ? 'bg-orange-500'
      : 'bg-green-500';

  return (
    <button
      data-miro-target="dashboard-pilotage-panel"
      onClick={onExpand}
      className={cn(
        // Position fixe bord droit, au-dessus du planning (top: 20%)
        'fixed right-0 top-[20%] -translate-y-1/2 z-50',
        // Dimensions
        'w-[90px] min-h-[280px]',
        // Style visuel
        'bg-card/95 backdrop-blur-md',
        'border border-r-0 border-border/50 border-b-2 border-b-blue-500/40 border-t-2 border-t-amber-500/50 rounded-l-2xl',
        'shadow-2xl shadow-black/30',
        // Contenu centré en colonne
        'flex flex-col items-center py-5 gap-3',
        // Hover effect
        'hover:w-[100px] hover:bg-card transition-all duration-200',
        'group cursor-pointer',
        // Animation bounce à l'arrivée
        shouldBounce && 'animate-subtle-bounce'
      )}
    >
      {/* ═══ ICÔNE + BADGE TOTAL ═══ */}
      <div className="relative">
        <div className="p-3 bg-orange-500/10 rounded-xl group-hover:bg-orange-500/20 transition-colors">
          <Target className="w-7 h-7 text-orange-500 group-hover:scale-110 transition-transform" />
        </div>
        {compteurs.total > 0 && (
          <span
            className={cn(
              'absolute -top-2 -right-2 w-6 h-6 text-white text-xs font-bold rounded-full flex items-center justify-center shadow-lg',
              badgeColor
            )}
          >
            {compteurs.total > 9 ? '9+' : compteurs.total}
          </span>
        )}
      </div>

      {/* ═══ TEXTE "PILOTAGE" VERTICAL ═══ */}
      <div
        className="text-xs font-bold tracking-widest text-white group-hover:text-white transition-colors"
        style={{ writingMode: 'vertical-rl', textOrientation: 'mixed' }}
      >
        PILOTAGE
      </div>

      {/* ═══ CHEVRON AFFORDANCE ═══ */}
      <ChevronLeft className="w-4 h-4 text-white/50 group-hover:text-white/80 transition-colors" />

      {/* ═══ SÉPARATEUR ═══ */}
      <div className="w-10 h-px bg-border/50" />

      {/* ═══ INDICATEURS PAR TYPE ═══ */}
      <div className="flex flex-col items-center gap-2">
        {/* Critique */}
        {compteurs.critique > 0 && (
          <div className="flex items-center gap-1.5 px-2 py-1 bg-red-500/10 rounded-lg">
            <AlertTriangle className="w-3.5 h-3.5 text-red-500" />
            <span className="text-xs font-bold text-red-500">{compteurs.critique}</span>
          </div>
        )}
        {/* Attention */}
        {compteurs.attention > 0 && (
          <div className="flex items-center gap-1.5 px-2 py-1 bg-orange-500/10 rounded-lg">
            <AlertCircle className="w-3.5 h-3.5 text-orange-500" />
            <span className="text-xs font-bold text-orange-500">{compteurs.attention}</span>
          </div>
        )}
        {/* Info */}
        {compteurs.info > 0 && (
          <div className="flex items-center gap-1.5 px-2 py-1 bg-blue-500/10 rounded-lg">
            <Info className="w-3.5 h-3.5 text-blue-500" />
            <span className="text-xs font-bold text-blue-500">{compteurs.info}</span>
          </div>
        )}
        {/* Aucune alerte */}
        {compteurs.total === 0 && (
          <div className="text-xs text-green-500 font-medium text-center px-2">
            Tout est OK
          </div>
        )}
      </div>
    </button>
  );
};

export default PilotagePanelCollapsed;
